import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Area, ComposedChart } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, AlertTriangle, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

// Mock forecasting data
const mockForecastData = {
  historical: [
    { month: 'Jul 2023', actual: 1247.32, forecast: null },
    { month: 'Aug 2023', actual: 1356.78, forecast: null },
    { month: 'Sep 2023', actual: 1189.45, forecast: null },
    { month: 'Oct 2023', actual: 1423.67, forecast: null },
    { month: 'Nov 2023', actual: 1298.91, forecast: null },
    { month: 'Dec 2023', actual: 1567.23, forecast: null },
    { month: 'Jan 2024', actual: 1391.17, forecast: 1420.35 },
    { month: 'Feb 2024', actual: null, forecast: 1456.89 },
    { month: 'Mar 2024', actual: null, forecast: 1523.45 },
    { month: 'Apr 2024', actual: null, forecast: 1598.12 },
    { month: 'May 2024', actual: null, forecast: 1675.78 },
    { month: 'Jun 2024', actual: null, forecast: 1756.34 }
  ],
  scenarios: {
    conservative: [
      { month: 'Feb 2024', cost: 1423.56 },
      { month: 'Mar 2024', cost: 1467.89 },
      { month: 'Apr 2024', cost: 1512.34 },
      { month: 'May 2024', cost: 1558.91 },
      { month: 'Jun 2024', cost: 1607.23 }
    ],
    optimistic: [
      { month: 'Feb 2024', cost: 1345.23 },
      { month: 'Mar 2024', cost: 1378.45 },
      { month: 'Apr 2024', cost: 1412.67 },
      { month: 'May 2024', cost: 1447.89 },
      { month: 'Jun 2024', cost: 1484.12 }
    ],
    pessimistic: [
      { month: 'Feb 2024', cost: 1567.89 },
      { month: 'Mar 2024', cost: 1634.23 },
      { month: 'Apr 2024', cost: 1703.45 },
      { month: 'May 2024', cost: 1775.67 },
      { month: 'Jun 2024', cost: 1850.89 }
    ]
  },
  budget: {
    monthly: 1500,
    yearly: 18000,
    currentYearSpent: 8347.67,
    projected: 17432.45
  },
  drivers: [
    { factor: 'User Growth', impact: 'high', trend: 15, description: 'Monthly active users increasing' },
    { factor: 'Model Costs', impact: 'medium', trend: -5, description: 'OpenAI pricing reduction' },
    { factor: 'Feature Usage', impact: 'high', trend: 23, description: 'New voice features adoption' },
    { factor: 'Efficiency Gains', impact: 'medium', trend: -12, description: 'Optimization implementations' }
  ]
};

interface ForecastScenarioProps {
  scenario: 'conservative' | 'optimistic' | 'pessimistic';
  data: Array<{ month: string; cost: number }>;
  selected: boolean;
  onSelect: () => void;
}

function ForecastScenario({ scenario, data, selected, onSelect }: ForecastScenarioProps) {
  const scenarioConfig = {
    conservative: { color: '#3b82f6', label: 'Conservative', icon: Target },
    optimistic: { color: '#10b981', label: 'Optimistic', icon: TrendingDown },
    pessimistic: { color: '#ef4444', label: 'Pessimistic', icon: TrendingUp }
  };

  const config = scenarioConfig[scenario];
  const Icon = config.icon;
  const finalCost = data[data.length - 1]?.cost || 0;
  const currentCost = 1391.17; // Current month
  const growth = ((finalCost - currentCost) / currentCost) * 100;

  return (
    <Card 
      className={`cursor-pointer transition-all ${selected ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4" style={{ color: config.color }} />
              <span className="font-medium text-sm">{config.label}</span>
            </div>
            {selected && (
              <Badge variant="default" className="text-xs">Selected</Badge>
            )}
          </div>
          
          <div>
            <div className="text-xl font-bold">${finalCost.toFixed(0)}</div>
            <div className="text-xs text-muted-foreground">June 2024 projection</div>
          </div>
          
          <div className={`text-xs flex items-center gap-1 ${growth > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {growth > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {growth > 0 ? '+' : ''}{growth.toFixed(1)}% vs current
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface CostDriverProps {
  driver: {
    factor: string;
    impact: 'low' | 'medium' | 'high';
    trend: number;
    description: string;
  };
}

function CostDriver({ driver }: CostDriverProps) {
  const impactColors = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-800'
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="space-y-1">
        <div className="font-medium text-sm">{driver.factor}</div>
        <div className="text-xs text-muted-foreground">{driver.description}</div>
      </div>
      
      <div className="flex items-center gap-2">
        <Badge className={impactColors[driver.impact]}>
          {driver.impact} impact
        </Badge>
        <div className={`text-sm font-mono ${driver.trend > 0 ? 'text-red-600' : 'text-green-600'}`}>
          {driver.trend > 0 ? '+' : ''}{driver.trend}%
        </div>
      </div>
    </div>
  );
}

export function CostForecasting() {
  const [selectedScenario, setSelectedScenario] = useState<'conservative' | 'optimistic' | 'pessimistic'>('conservative');
  const [timeHorizon, setTimeHorizon] = useState('6m');
  
  const data = mockForecastData;

  const budgetStatus = {
    remaining: data.budget.yearly - data.budget.currentYearSpent,
    percentUsed: (data.budget.currentYearSpent / data.budget.yearly) * 100,
    onTrack: data.budget.projected <= data.budget.yearly
  };

  return (
    <div className="space-y-6">
      {/* Budget Overview */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Annual Budget</div>
              <div className="text-2xl font-bold">${data.budget.yearly.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Total allocated</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Spent YTD</div>
              <div className="text-2xl font-bold">${data.budget.currentYearSpent.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">
                {budgetStatus.percentUsed.toFixed(1)}% of budget
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Projected Total</div>
              <div className={`text-2xl font-bold ${budgetStatus.onTrack ? 'text-green-600' : 'text-red-600'}`}>
                ${data.budget.projected.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {budgetStatus.onTrack ? 'Under budget' : 'Over budget'}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Remaining</div>
              <div className={`text-2xl font-bold ${budgetStatus.remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${Math.abs(budgetStatus.remaining).toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {budgetStatus.remaining > 0 ? 'Available' : 'Overrun'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forecast Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Cost Forecast</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={timeHorizon} onValueChange={setTimeHorizon}>
                <SelectTrigger className="w-32 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3m">3 months</SelectItem>
                  <SelectItem value="6m">6 months</SelectItem>
                  <SelectItem value="12m">12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={data.historical}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 10 }}
                stroke="#6b7280"
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                stroke="#6b7280"
                label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                formatter={(value: number, name: string) => [
                  value ? `$${value.toFixed(2)}` : 'N/A', 
                  name === 'actual' ? 'Actual' : 'Forecast'
                ]}
              />
              
              {/* Historical line */}
              <Line 
                type="monotone" 
                dataKey="actual" 
                stroke="#3b82f6" 
                strokeWidth={3}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                connectNulls={false}
              />
              
              {/* Forecast line */}
              <Line 
                type="monotone" 
                dataKey="forecast" 
                stroke="#f59e0b" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                connectNulls={false}
              />
              
              {/* Budget line */}
              <Line 
                type="monotone" 
                dataKey={() => data.budget.monthly}
                stroke="#ef4444" 
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                name="Budget"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Scenario Analysis */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Scenario Analysis</h3>
            <p className="text-sm text-muted-foreground">
              Compare different cost projections based on various assumptions
            </p>
          </div>
          <Button size="sm" variant="outline">
            Export Forecasts
          </Button>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(data.scenarios).map(([scenario, scenarioData]) => (
            <ForecastScenario
              key={scenario}
              scenario={scenario as any}
              data={scenarioData}
              selected={selectedScenario === scenario}
              onSelect={() => setSelectedScenario(scenario as any)}
            />
          ))}
        </div>
      </div>

      {/* Cost Drivers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost Drivers Analysis</CardTitle>
          <div className="text-sm text-muted-foreground">
            Key factors influencing future cost trends
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.drivers.map((driver, index) => (
              <CostDriver key={index} driver={driver} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Budget Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Budget Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {!budgetStatus.onTrack && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="font-medium text-red-800">Budget Overrun Alert</span>
                </div>
                <p className="text-sm text-red-700 mt-1">
                  Current projection exceeds annual budget by ${(data.budget.projected - data.budget.yearly).toLocaleString()}
                </p>
              </div>
            )}
            
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <span className="font-medium text-yellow-800">High Growth Rate</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                Voice feature adoption driving 23% monthly cost increase
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}